import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { InterviewType, resolveInterviewType } from './functions'

import { useSupport } from '@/Providers'
import { FormNavigation } from '@/components/form-controls'
import { Button, Spinner } from '@/components/nextui'
import { useAuthHook } from '@/hooks'
import { useAspirantsApi } from '@/services/api/aspirantsApi'
import { useAuthStore } from '@/store/authStore'
import { ErrorHandlingStrategy, MessageLevel } from '@/types/api'
import { FormsGroupsProps } from '@/types/forms/formStatus'

type BookingType = Exclude<InterviewType, 'both' | 'unknown'>

const BOOKINGS: Record<BookingType, { url: string; title: string }> = {
    virtual: {
        url: import.meta.env.VITE_BOOKING_VIRTUAL_URL,
        title: 'Entrevista Virtual'
    },
    onSite: {
        url: import.meta.env.VITE_BOOKING_ONSITE_URL,
        title: 'Entrevista Presencial'
    }
}

export const InterviewForm = ({
    onNext: _onNext,
    onPrevious: _onPrevious,
    hasNextStep
}: FormsGroupsProps): JSX.Element => {
    const { t } = useTranslation()
    const { idAspirant } = useAuthHook()
    const aspirantsApi = useAspirantsApi({
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY,
        errorHandling: ErrorHandlingStrategy.RETURN
    })

    const { idInscription, studyModality } = useAuthStore()
    const { openSupport } = useSupport()

    const [interviewType, setInterviewType] = useState<InterviewType | null>(
        null
    )
    const [loadError, setLoadError] = useState(false)
    const [reloadKey, setReloadKey] = useState(0)
    const [selectedBooking, setSelectedBooking] =
        useState<BookingType>('onSite')

    // El tipo de entrevista depende de la modalidad final del programa (educationModality),
    // con la jornada (studyModality) como respaldo
    useEffect(() => {
        if (!idAspirant) {
            return
        }

        const loadInterviewType = async (): Promise<void> => {
            setInterviewType(null)
            setLoadError(false)
            try {
                const { data, status } =
                    await aspirantsApi.getAcademicAdmission(
                        idAspirant,
                        idInscription
                    )

                if (status !== 200) {
                    setLoadError(true)

                    return
                }

                setInterviewType(
                    resolveInterviewType(
                        data?.educationModality,
                        data?.studyModality ?? studyModality
                    )
                )
            } catch {
                setLoadError(true)
            }
        }

        loadInterviewType()
    }, [idAspirant, idInscription, reloadKey])

    const iframeProps = {
        width: '100%',
        height: '100%',
        scrolling: 'yes' as const,
        style: { border: 0, minHeight: '80vh' }
    }

    const renderBooking = (): JSX.Element => {
        if (loadError) {
            return (
                <div className="flex flex-col items-center gap-4 py-10 text-center">
                    <p className="text-default-500">
                        No pudimos cargar la información de tu entrevista. Por
                        favor intenta de nuevo.
                    </p>
                    <Button
                        color="primary"
                        onPress={() => setReloadKey((key) => key + 1)}
                    >
                        Reintentar
                    </Button>
                </div>
            )
        }

        if (interviewType === null) {
            return (
                <div className="flex justify-center py-10">
                    <Spinner color="primary" />
                </div>
            )
        }

        if (interviewType === 'unknown') {
            return (
                <div className="flex flex-col items-center gap-4 py-10 text-center">
                    <p className="text-default-500">
                        No pudimos determinar la modalidad de tu programa. Por
                        favor contacta a soporte para agendar tu entrevista.
                    </p>
                    <div className="flex gap-2">
                        <Button
                            color="default"
                            onPress={() => setReloadKey((key) => key + 1)}
                        >
                            Reintentar
                        </Button>
                        <Button
                            color="primary"
                            onPress={openSupport}
                        >
                            Contactar soporte
                        </Button>
                    </div>
                </div>
            )
        }

        const bookingType =
            interviewType === 'both' ? selectedBooking : interviewType
        const booking = BOOKINGS[bookingType]

        return (
            <>
                {interviewType === 'both' && (
                    <div className="flex gap-2">
                        {(Object.keys(BOOKINGS) as BookingType[]).map(
                            (type) => (
                                <Button
                                    key={type}
                                    color={
                                        selectedBooking === type
                                            ? 'primary'
                                            : 'default'
                                    }
                                    onPress={() => setSelectedBooking(type)}
                                >
                                    {BOOKINGS[type].title}
                                </Button>
                            )
                        )}
                    </div>
                )}
                <iframe
                    key={bookingType}
                    src={booking.url}
                    title={booking.title}
                    {...iframeProps}
                />
            </>
        )
    }

    return (
        <div className="flex flex-col w-full h-full gap-4">
            {renderBooking()}
            <FormNavigation
                onSubmit={() => _onNext && _onNext()}
                onPrevious={_onPrevious}
                progress={50}
                submitLabel={t('forms.ui.next')}
                hideSubmit={hasNextStep === false}
            />
        </div>
    )
}
